<template>
  <div class="modern-master-view pb-12">
    <!-- Global Header -->
    <v-row class="mb-8 align-center px-4 pt-4">
      <div class="d-flex align-center">
        <v-avatar color="primary" size="48" class="mr-4 shadow-sm border-white-sm">
          <v-icon color="white">fas fa-shopping-basket</v-icon>
        </v-avatar>
        <div>
          <div class="text-h4 font-weight-black primary--text lh-1">Ordenes de Compra</div>
          <div class="caption grey--text font-weight-bold uppercase tracking-widest mt-1">Gestión y tracking de abastecimiento</div>
        </div>
      </div>
      <v-spacer></v-spacer>
      <div class="d-flex align-center gap-3">
        <export-purchase-orders 
          :disabled="loading" 
          :filter="filter" 
          :search="search"
          :options="options"
          :estatuses="estatuses"
        />
        <Can I="create" a="PurchaseOrder">
          <v-btn 
            color="success" 
            class="font-weight-black text-uppercase tracking-widest rounded-lg px-8 h-44 shadow-sm" 
            @click="$router.push({name:'CreatePurchaseOrder',params:{id:-1}})"
          >
            <v-icon left small>fas fa-plus</v-icon> Nueva Orden
          </v-btn>
        </Can>
      </div>
    </v-row>

    <!-- Filters Section -->
    <v-card outlined class="modern-card shadow-sm pa-4 mb-8">
      <v-row dense align="center">
        <v-col cols="12" md="2">
          <div class="caption grey--text font-weight-black text-uppercase mb-1 ml-1">Desde</div>
          <v-text-field 
            hide-details 
            :disabled="loading" 
            dense 
            v-model='filter.created_after' 
            outlined 
            clearable 
            type="date" 
            class="modern-input"
          />
        </v-col>
        <v-col cols="12" md="2">
          <div class="caption grey--text font-weight-black text-uppercase mb-1 ml-1">Hasta</div>
          <v-text-field 
            hide-details 
            :disabled="loading" 
            dense 
            v-model='filter.created_before' 
            clearable 
            outlined 
            type="date" 
            class="modern-input"
          />
        </v-col>
        <v-col cols="12" md="3">
          <div class="caption grey--text font-weight-black text-uppercase mb-1 ml-1">Estatus</div>
          <v-select
            v-model="filter.estatus"
            :items="estatuses"
            item-text="text"
            item-value="value"
            placeholder="Todos los estatus"
            outlined
            dense
            clearable
            hide-details
            class="modern-input"
            prepend-inner-icon="fas fa-filter"
          />
        </v-col>
        <v-col cols="12" md="3">
          <div class="caption grey--text font-weight-black text-uppercase mb-1 ml-1">Tipo</div>
          <v-select
            v-model="filter.tipo"
            :items="tipos"
            item-text="text"
            item-value="value"
            placeholder="Todos los tipos"
            outlined
            dense
            clearable
            hide-details
            class="modern-input"
            prepend-inner-icon="fas fa-boxes"
          />
        </v-col>
        <v-col cols="12" md="2" class="d-flex align-end pb-1">
          <v-btn block color="primary" outlined class="font-weight-black" @click="fetch" :loading="loading">
            <v-icon left small>fas fa-sync</v-icon> Actualizar
          </v-btn>
        </v-col>
      </v-row>
    </v-card>

    <v-card outlined class="modern-card shadow-sm overflow-hidden">
      <v-data-table
        :headers="computedHeaders"
        :items="orders"
        :loading="loading"
        :options.sync="options"
        :server-items-length="parseInt(total)"
        class="modern-table"
        @click:row='displayDetails'
        :footer-props="{
          showFirstLastPage: true,
          itemsPerPageOptions: [10, 20, 30, 40, 50, 100],
        }"
      >
        <template v-slot:top>
          <div class="px-6 py-4 border-bottom bg-lightgrey d-flex align-center">
            <span class="caption font-weight-black uppercase tracking-widest grey--text">Listado de Órdenes</span>
            <v-spacer></v-spacer>
            <v-text-field
              v-model="search"
              @keyup="searchTimeOut"
              placeholder="Buscar por número o proveedor..."
              prepend-inner-icon="fas fa-search"
              outlined
              dense
              hide-details
              clearable
              :disabled="loading"
              class="modern-search-field"
            ></v-text-field>
          </div>
        </template>
          
          <template v-slot:item.id="{ item }">
            <div class="d-flex flex-column py-2">
              <span class="body-2 font-weight-black primary--text lh-1 mb-1">#{{ item.id }}</span>
              <div class="d-flex align-center">
                <v-icon x-small :color="item.stock_service ? 'primary' : 'secondary'" class="mr-1">
                  {{ item.stock_service ? 'fas fa-boxes' : 'fas fa-tools' }}
                </v-icon>
                <span class="caption grey--text font-weight-bold uppercase">{{ item.stock_service ? 'Inv' : 'Serv' }}</span>
              </div>
            </div>
          </template>
          
          <template v-slot:item.created_at="{ item }">
            <span class="body-2 grey--text text--darken-2 font-weight-medium">
              {{ item.created_at | moment('DD/MM/YYYY') }}
            </span>
          </template>
          
          <template v-slot:item.net_total="{ item }">
            <span class="body-2 font-weight-black primary--text">
              {{ totalOrder(item) | moneda }}
            </span>
          </template>
          
          <template v-slot:item.estatus="{ item }">
            <v-chip small label dark :color="getStatusColor(item)" class="font-weight-black shadow-xs-primary px-3">
              <v-icon x-small left>{{ item.estatus == 2 ? 'fas fa-check-double' : (item.estatus == 3 ? 'fas fa-ban' : 'fas fa-clock') }}</v-icon>
              {{ getStatusName(item) }}
            </v-chip>
          </template>

          <template v-slot:item.actions="{ item }">
            <v-menu bottom left offset-y transition="slide-y-transition" content-class="modern-card">
              <template v-slot:activator="{ on, attrs }">
                <v-btn icon v-bind="attrs" v-on="on" class="grey lighten-4" @click.stop>
                  <v-icon small color="grey darken-2">fas fa-ellipsis-vertical</v-icon>
                </v-btn>
              </template>
              <v-list dense class="py-1">
                <Can I="update" a="PurchaseOrder">
                  <v-list-item v-if="!Boolean(item.estatus>1)" @click.stop="$router.push({name:'CreatePurchaseOrder',params:{id:item.id}})">
                    <v-list-item-icon class="mr-2">
                      <v-icon x-small color="primary">fas fa-edit</v-icon>
                    </v-list-item-icon>
                    <v-list-item-title class="caption font-weight-bold">Modificar</v-list-item-title>
                  </v-list-item>
                </Can>
                
                <Can I="aproove" a="PurchaseOrder">
                  <v-list-item v-if="!Boolean(item.estatus>1)" @click.stop="()=>{ toApprove=item; showConfirmApprove=true; }">
                    <v-list-item-icon class="mr-2">
                      <v-icon x-small color="success">fas fa-clipboard-check</v-icon>
                    </v-list-item-icon>
                    <v-list-item-title class="caption font-weight-bold success--text">Aprobar</v-list-item-title>
                  </v-list-item>
                </Can>

                <Can I="revert" a="PurchaseOrder">
                  <v-list-item v-if="Boolean(item.estatus>1&&item.estatus!=3)" @click.stop="()=>{ toRevert=item; showConfirmRevert=true; }">
                    <v-list-item-icon class="mr-2">
                      <v-icon x-small color="warning">fas fa-undo</v-icon>
                    </v-list-item-icon>
                    <v-list-item-title class="caption font-weight-bold warning--text text--darken-3">Revertir</v-list-item-title>
                  </v-list-item>
                </Can>

                <Can I="print" a="PurchaseOrder">
                  <v-list-item v-if="Boolean(item.estatus>1&&item.estatus!=3)" @click.stop="print(item)">
                    <v-list-item-icon class="mr-2">
                      <v-icon x-small color="grey darken-1">fas fa-print</v-icon>
                    </v-list-item-icon>
                    <v-list-item-title class="caption font-weight-bold">Imprimir</v-list-item-title>
                  </v-list-item>
                </Can>

                <Can I="create" a="PurchaseOrder">
                  <v-list-item @click.stop="$router.push({name:'CreatePurchaseOrder',params:{id:item.id, duplicate:true}})">
                    <v-list-item-icon class="mr-2">
                      <v-icon x-small color="info">fas fa-clone</v-icon>
                    </v-list-item-icon>
                    <v-list-item-title class="caption font-weight-bold">Duplicar</v-list-item-title>
                  </v-list-item>
                </Can>

                <v-divider class="my-1"></v-divider>

                <Can I="update" a="PurchaseOrder">
                  <v-list-item @click.stop="()=>{ toLinkOt=true; purchaseOrderToLink=item; }">
                    <v-list-item-icon class="mr-2">
                      <v-icon x-small color="primary">fas fa-link</v-icon>
                    </v-list-item-icon>
                    <v-list-item-title class="caption font-weight-bold">Vincular OT</v-list-item-title>
                  </v-list-item>
                </Can>
                
                <Can I="update" a="PurchaseOrder">
                  <v-list-item v-if="Boolean(item.estatus>1&&item.estatus<3)" @click.stop="()=>{ order=item; showConfirmReceived=true; }">
                    <v-list-item-icon class="mr-2">
                      <v-icon x-small color="secondary">fas fa-dolly</v-icon>
                    </v-list-item-icon>
                    <v-list-item-title class="caption font-weight-bold secondary--text">Recepcionar</v-list-item-title>
                  </v-list-item>

                  <v-list-item v-if="Boolean(item.estatus==4)" @click.stop="()=>{ order=item; showConfirmRevertReceived=true; }">
                    <v-list-item-icon class="mr-2">
                      <v-icon x-small color="error">fas fa-dolly-flatbed</v-icon>
                    </v-list-item-icon>
                    <v-list-item-title class="caption font-weight-bold error--text text--darken-3">Anular Recepción</v-list-item-title>
                  </v-list-item>  
                </Can>

                <Can I="delete" a="PurchaseOrder">
                  <v-list-item v-if="Boolean(item.estatus<3)" @click.stop="()=>{ order=item; showConfirmCancel=true; }">
                    <v-list-item-icon class="mr-2">
                      <v-icon x-small color="error">fas fa-trash</v-icon>
                    </v-list-item-icon>
                    <v-list-item-title class="caption font-weight-bold error--text">Anular</v-list-item-title>
                  </v-list-item> 
                </Can>
              </v-list>
            </v-menu>
          </template>
      </v-data-table>
    </v-card>

    <confirm
      v-if="toApprove"
      v-model="showConfirmApprove" 
      :message="`Confirma la aprobación de la Orden #${toApprove.id} emitida a ${toApprove.provider_name} por un total neto de ${formatCurrency(totalOrder(toApprove))}?`" 
      @ok="approve"
      @cancel="showConfirmApprove=false"
    />
    <confirm
      v-if="toRevert"
      v-model="showConfirmRevert" 
      :message="`Confirma la reversion de la Orden #${toRevert.id} emitida a ${toRevert.provider_name} por un total neto de ${formatCurrency(totalOrder(toRevert))}?`" 
      @ok="revert"
      @cancel="showConfirmRevert=false"
    />
    <confirm
      v-if="order"
      v-model="showConfirmCancel" 
      :message="`Confirma la anulación de la Orden #${order.id} emitida a ${order.provider_name}?`" 
      @ok="cancel"
      @cancel="showConfirmCancel=false"
    />
    <confirm
      v-if="order"
      v-model="showConfirmReceived" 
      :message="`Confirma el cambio de Estatus de la Orden #${order.id} emitida a ${order.provider_name} a Recepcionada?`" 
      @ok="receive"
      @cancel="showConfirmReceived=false"
    />
    <confirm
      v-if="order"
      v-model="showConfirmRevertReceived" 
      :message="`Confirma el cambio de Estatus de la Orden #${order.id} emitida a ${order.provider_name} a NO Recepcionada?`" 
      @ok="revertReceive"
      @cancel="showConfirmRevertReceived=false"
    />

    <v-dialog v-model="showPurchaseOrder" max-width="1200">
      <v-card class="pa-6">  
        <detailed-purchase-order :purchaseOrder="purchaseOrderToDisplay" @close="showPurchaseOrder=false"/>
      </v-card>
    </v-dialog>

    <v-dialog v-model="toLinkOt" max-width="500">
      <v-card class="pa-6">
        <v-card-title class="titulo text-h5">Vincular Orden de Trabajo</v-card-title>
        <LinkPOtoOT
          :purchase_order="purchaseOrderToLink"
          @updated="()=>{
            this.toLinkOt=false
            this.fetch()
          }"
        />
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import {fetchPurchase, updatePurchase} from '@/api/stocks'
import notifications from '@/utils/notifications'
import ExportPurchaseOrders from '@/components/stocks/ExportPurchaseOrders'
import LinkPOtoOT from '@/components/stocks/LinkPOtoOT'
import DetailedPurchaseOrder from '@/components/stocks/DetailedPurchaseOrder'
import {pdfPurchaseOrder} from '@/doc_generators/purchaseOrder'
import {moneda} from '@/utils/filtros'
import moment from 'moment'

export default {
  name:'PurchaseOrders',
  components:{ExportPurchaseOrders, DetailedPurchaseOrder, LinkPOtoOT},
  data() {
    return {
      loading: false,
      loadingPurchaseOrder:false,
      showPurchaseOrder:false,
      purchaseOrderToDisplay:'',
      purchaseOrderToLink:'',
      orders:[],
      order:'',
      timer:'',
      options:{},
      search:'',
      total:'',
      showDialog:false,
      showConfirmApprove:false,
      showConfirmCancel:false,
      showConfirmReceived:false,
      showConfirmRevert:false,
      showConfirmRevertReceived:false,
      toApprove:'',
      toRevert:'',
      toLinkOt:false,
      work_order_for_link:'',
      estatuses:[
        {
          value:1,
          text:'Ingresada',
          color:'info'
        },
        {
          value:2,
          text:'Aprobada',
          color:'success'
        },
        {
          value:4,
          text:'Recepcionada',
          color:''
        },
        {
          value:3,
          text:'Anulada',
          color:'error'
        },
      ],
      tipos:[
        {
          value:'true',
          text:'Inventario'
        },
        {
          value:'false',
          text:'Servicios'
        },
      ],
      filter:{
        created_after:moment(new Date(new Date().getFullYear(), new Date().getMonth(), 1)).format('yyyy-MM-DD'),
        created_before:'',
        estatus:'',
        tipo:'',
      },
      headers: [
        { text: 'Número', align: 'end', value: 'id', class: 'table-header', show:true },
        { text: 'Creada', align: 'start', value: 'created_at', class: 'table-header', show:true },
        { text: 'Proveedor', align: 'start', value: 'provider_name', class: 'table-header', show:true },
        { text: 'Referencia', align: 'start', value: 'title', class: 'table-header', show:true },
        { text: 'C. Costo', align: 'start', value: 'center_name', class: 'table-header', show:true },
        { text: 'OT', align: 'start', value: 'work_order', class: 'table-header', sortable:false, show:true },
        { text: 'Neto', align: 'end', value: 'net_total', class: 'table-header', show:true },
        { text: 'Estatus', align: 'center', value: 'estatus', class: 'table-header', show:true },
        { text: '', align: 'center', value: 'actions', class: 'table-header', show:true },
      ],
    }
  },
  methods:{
    formatCurrency(val) {
      const code = this.$store.getters.getCompany?.country_code || 'cl'
      return moneda(val, code)
    },
    totalOrder(item){
      const total = item.details.reduce((acc,val)=>{return acc + parseFloat(val.qty)*parseFloat(val.price);},0)
      return total - total * parseFloat(item.discount || 0) / 100
    },
    getStatusName(item){
      const status = this.estatuses.find(i=>i.value==item.estatus)
      return status ? status.text : 'Desconocido'
    },
    getStatusColor(item){
      const status = this.estatuses.find(i=>i.value==item.estatus)
      return status ? status.color : 'grey'
    },
    displayDetails(item){
      this.purchaseOrderToDisplay=item
      this.showPurchaseOrder=true
    },
    print(item){
      pdfPurchaseOrder(item)
    },
    async fetch(){
      this.loading=true
      if(!this.filter.created_after) this.filter.created_after=''
      if(!this.filter.created_before) this.filter.created_before=''
      
      const {sortBy, sortDesc, page, itemsPerPage }=this.options
      let sort=''
      if(sortBy && sortBy[0]=='center_name'){ sort = 'center__name' }
      else if(sortBy && sortBy[0]=='provider_name'){ sort = 'provider__name' }
      else { sort = (sortBy && sortBy[0]) || '-id' }

      if (!this.filter.estatus) this.filter.estatus=''
      if (!this.filter.tipo) this.filter.tipo=''

      const query=`ordering=${sortDesc && sortDesc[0] ? sort : '-' + sort}&page=${page || 1}&page_size=${itemsPerPage || 20}&search=${this.search}&estatus=${this.filter.estatus}&stock_service=${this.filter.tipo}&created_after=${this.filter.created_after}&created_before=${this.filter.created_before}`
      
      await fetchPurchase(query)
      .then(res=>{
        this.total=res.count
        this.orders=res.results
      })
      .catch((err)=>{
        console.error(err)
        notifications.showError('Error cargando órdenes')
      })
      this.loading=false
    },
    searchTimeOut() {  
      if (this.timer) {
          clearTimeout(this.timer);
      }
      this.timer = setTimeout(() => {
          this.fetch()
      }, 700);
    },
    async approve(){
      if(!this.toApprove) return
      await updatePurchase(this.toApprove.id,'','approve=true')
      .then(res=>{
        this.showConfirmApprove=false
        this.toApprove=''
        notifications.showSuccess('Orden de Compra #'+res.id+' aprobada!')
        this.fetch()
      })
      .catch(err=>{
        notifications.showError('Error aprobando orden de compra')
      })
    },
    async revert(){
      if(!this.toRevert) return
      await updatePurchase(this.toRevert.id,'','revert=true')
      .then(res=>{
        this.showConfirmRevert=false
        this.toRevert=''
        notifications.showSuccess('Orden de Compra #'+res.id+' revertida!')
        this.fetch()
      })
      .catch(err=>{
        notifications.showError('Error revertiendo orden de compra')
      })
    },
    async cancel(){
      if(!this.order) return
      await updatePurchase(this.order.id,'','cancel=true')
      .then(res=>{
        this.showConfirmCancel=false  
        this.order=''
        notifications.showSuccess('Orden de Compra #'+res.id+' cancelada!')
        this.fetch()
      })
      .catch(err=>{
        notifications.showError('Error cancelando orden de compra')
      })
    },
    async receive(){
      if(!this.order) return
      await updatePurchase(this.order.id,'','receive=true')
      .then(res=>{
        this.showConfirmReceived=false  
        this.order=''
        notifications.showSuccess('Orden de Compra #'+res.id+' Recibida!')
        this.fetch()
      })
      .catch(err=>{
        notifications.showError('Error cambiando estatus a orden de compra')
      })
    },
    async revertReceive(){
      if(!this.order) return
      await updatePurchase(this.order.id,'','receive=false')
      .then(res=>{
        this.showConfirmRevertReceived=false  
        this.order=''
        notifications.showSuccess('Orden de Compra #'+res.id+' Revertida!')
        this.fetch()
      })
      .catch(err=>{
        notifications.showError('Error revirtiendo recepción')
        this.showConfirmRevertReceived=false
        this.order=''
      })
    },
  },
  watch:{
    options:{
      handler:'fetch',
      deep:true
    },
    filter:{
      handler:'fetch',
      deep:true
    }
  },
  mounted(){
    this.fetch()
  },
  computed:{
    computedHeaders(){
      return this.headers.filter(h => h.show)
    }
  }
}
</script>

<style scoped>
.modern-master-view { background-color: #f4f7f9; min-height: 100vh; }
.modern-card { border-radius: 12px !important; border: 1px solid rgba(0,0,0,0.05) !important; background-color: white !important; }
.shadow-sm { box-shadow: 0 4px 12px rgba(0,0,0,0.04) !important; }
.shadow-xs { box-shadow: 0 1px 3px rgba(0,0,0,0.05) !important; }
.shadow-xs-primary { box-shadow: 0 2px 4px rgba(var(--v-primary-base), 0.15) !important; }
.border-white-sm { border: 2px solid white !important; }
.border-bottom { border-bottom: 1px solid rgba(0,0,0,0.05) !important; }
.lh-1 { line-height: 1 !important; }
.gap-2 { gap: 8px; }
.uppercase { text-transform: uppercase; }
.tracking-widest { letter-spacing: 1.8px; }
.gap-3 { gap: 12px; }

.modern-input >>> .v-input__control fieldset { border-radius: 8px !important; border-color: #e0e4e8 !important; }
.modern-input >>> .v-input__control { background-color: white !important; }
.modern-input >>> input { font-size: 0.9rem !important; font-weight: 500 !important; color: #1a1f2e !important; }

.modern-search-field { width: 320px; }
.modern-search-field >>> .v-input__control fieldset { border-radius: 24px !important; border-color: #e0e4e8 !important; background-color: white !important; }
.modern-search-field >>> .v-input__control { box-shadow: 0 2px 8px rgba(0,0,0,0.04) !important; }

.modern-table >>> thead th { background-color: #f8fbfc !important; height: 56px !important; text-transform: uppercase; font-size: 0.75rem !important; letter-spacing: 1px; font-weight: 900 !important; color: #607d8b !important; }
.modern-table >>> td { height: 72px !important; border-bottom: 1px solid #f0f3f5 !important; }
.modern-table >>> tr:hover { background-color: #f4f7f9 !important; cursor: pointer; }

.v-btn.shadow-sm { box-shadow: 0 4px 12px rgba(var(--v-success-base), 0.2) !important; }
.h-44 { height: 44px !important; }
.text-uppercase { text-transform: uppercase !important; }
</style>
